import {
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  handleAddRemoveVariable,
  handleBasicsChange,
  handleBasicsSwitchChange,
  handleCalculationBlur,
  handleDefaultVarChange,
  handleCalculationResponseTypeChange,
} from "../../../Redux/reducers/questionSlice";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { requiredStyles } from "../question.style";
import { DeleteIcon } from "../Icons";

type Props = {
  question: any;
};
export const TemplateTab = ({ question }: Props) => {
  const dispatch = useAppDispatch();

  const isPreview = useAppSelector(({ question }) => !question.editable);
  const isTemplateError = useAppSelector(
    ({ question }) => question.isTemplateError
  );
  const formCategory = useAppSelector(({ question }) => question.formCategory);

  const varnameScopeHelper =
    formCategory === "survey"
      ? "You can use {varname} for dynamic values from this form or a screener form in this study."
      : formCategory === "screening"
        ? "You can use {varname} for dynamic values from this form only."
        : null;

  const onChange = (
    key: string,
    value: string | boolean,
    isProp?: boolean,
    convertToNum?: boolean
  ) => {
    if (!isPreview)
      dispatch(
        handleBasicsChange({
          key,
          value,
          isProp,
          convertToNum,
        })
      );
  };

  const handlePrefillDefaults = (value: string) => {
    dispatch(handleCalculationBlur(value));
  };

  const handleDefaultsChange = (
    index: number,
    value: string,
    section: string
  ) => {
    dispatch(handleDefaultVarChange({ index, value, section }));
  };

  const onSwitch = (key: string, event: any) => {
    dispatch(handleBasicsSwitchChange({ key, value: event.target.checked }));
  };

  return (
    <Stack gap={2} py={1}>
      {isTemplateError && (
        <Typography variant="caption" fontWeight="medium" color={"red"}>
          {isTemplateError}
        </Typography>
      )}

      <Stack gap={1}>
        <Typography variant="body1" fontWeight={"regular"} sx={requiredStyles}>
          Calculation Template
        </Typography>
        <TextField
          name="calculationTemplate"
          fullWidth
          multiline
          rows={5}
          placeholder="Calculation Template"
          value={question.calculationTemplate || ""}
          onChange={(e) => onChange("calculationTemplate", e.target.value)}
          InputProps={{
            readOnly: isPreview,
          }}
        />
        {varnameScopeHelper && (
          <Typography variant="caption" color="text.secondary">
            {varnameScopeHelper}
          </Typography>
        )}
      </Stack>
      <Stack direction="row" sx={{ justifyContent: "space-between" }}>
        <Typography variant="body1" fontWeight={"regular"}>
          Defaults
        </Typography>
        <Button
          variant="contained"
          onClick={() => handlePrefillDefaults(question.calculationTemplate)}
          sx={{ ml: 2 }}
          disabled={!question.calculationTemplate.trim()}
        >
          Prefill
        </Button>
      </Stack>
      {question.properties?.defaults?.length === 0 && (
        <Typography
          sx={{ fontSize: 16, fontWeight: "500", color: "text.secondary" }}
        >
          No Defaults added
        </Typography>
      )}

      {question?.properties?.defaults?.length > 0 &&
        question?.properties?.defaults?.map((item: any, index: number) => {
          return (
            <Stack gap={1} key={index} direction="row">
              <Stack gap={1} flex={1}>
                <Typography variant="body1" fontWeight={"regular"}>
                  Variable Name
                </Typography>
                <TextField
                  name={`varname-${index}`}
                  fullWidth
                  value={item.varname || ""}
                  inputProps={{ readOnly: true }}
                />
              </Stack>

              <Stack gap={1} flex={1}>
                <Typography variant="body1" fontWeight={"regular"}>
                  Type
                </Typography>
                <FormControl fullWidth>
                  <Select
                    readOnly={isPreview}
                    fullWidth
                    id={`type-${index}`}
                    value={item.type || "string"}
                    onChange={(e) =>
                      handleDefaultsChange(index, e.target.value, "type")
                    }
                  >
                    <MenuItem value={"number"}>Number</MenuItem>
                    <MenuItem value={"string"}>Text</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <Stack gap={1} flex={1}>
                <Typography
                  variant="body1"
                  fontWeight={"regular"}
                  sx={item.type === "number" ? requiredStyles : {}}
                >
                  Value
                </Typography>
                <TextField
                  type={item.type === "number" ? "number" : "text"}
                  name={`value-${index}`}
                  fullWidth
                  value={item.value}
                  onChange={(e) =>
                    handleDefaultsChange(index, e.target.value, "value")
                  }
                />
              </Stack>

              {!isPreview && (
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  gap={1}
                  sx={{ width: "50px" }}
                  mt={4}
                >
                  {/* <IconButton
                    aria-label="add"
                    size="small"
                    onClick={() =>
                      dispatch(
                        handleAddRemoveVariable({
                          actionType: "add",
                          actionIndex: index,
                        })
                      )
                    }
                    color="primary"
                  >
                    <Add fontSize="small" />
                  </IconButton> */}

                  <IconButton
                    aria-label="delete"
                    size="small"
                    color="error"
                    onClick={() =>
                      dispatch(
                        handleAddRemoveVariable({
                          actionType: "remove",
                          actionIndex: index,
                        })
                      )
                    }
                    sx={{ height: 40, width: 40 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              )}
            </Stack>
          );
        })}

      <Stack gap={1}>
        <FormControlLabel
          disabled={isPreview}
          control={
            <Switch
              checked={question.properties.isHidden}
              onChange={(e) => onSwitch("isHidden", e)}
            />
          }
          label={
            <Typography variant="body1" fontWeight={"regular"}>
              Is Hidden
            </Typography>
          }
        />
      </Stack>

      <Stack gap={1}>
        <Typography variant="body1" fontWeight={"regular"}>
          Response Type
        </Typography>
        <FormControl fullWidth>
          <Select
            readOnly={isPreview}
            fullWidth
            id="response-type"
            value={question.properties.responseType || "number"}
            onChange={(e) => {
              onChange("responseType", e.target.value, true);
              dispatch(
                handleCalculationResponseTypeChange({
                  responseType: e.target.value,
                })
              );
            }}
          >
            <MenuItem value={"number"}>Number</MenuItem>
            <MenuItem value={"string"}>Text</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Stack gap={1}>
        <Typography variant="body1" fontWeight={"regular"}>
          Execution Type
        </Typography>
        <FormControl fullWidth>
          <Select
            readOnly={isPreview}
            fullWidth
            id="execution-type"
            value={question.properties.executionType || "auto"}
            onChange={(e) => onChange("executionType", e.target.value, true)}
          >
            <MenuItem value={"manual"}>Manual</MenuItem>
            <MenuItem value={"auto"}>Auto</MenuItem>
          </Select>
        </FormControl>
      </Stack>
    </Stack>
  );
};
